.button {
  --btn-bg: var(--brand-solid);
  --btn-text: var(--fg-on-solid);
  --btn-border: transparent;
  --btn-icon-size: 1.25rem;
  --btn-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05);
  --btn-focus-ring: var(--focus-ring);

  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  border-radius: 0.5rem;
  background: var(--btn-bg);
  color: var(--btn-text);
  border: 1px solid var(--btn-border);
  box-shadow: var(--btn-shadow);
  font-family: var(--font-family-base);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition:
    background 150ms ease,
    border-color 150ms ease,
    box-shadow 150ms ease;
  position: relative;
  overflow: clip;
  white-space: nowrap;
}

.button:disabled {
  cursor: not-allowed;
}

/* Keyboard focus (Tab) — WCAG 2.4.11 focus-ring token; transparent outline keeps
   a visible indicator under forced-colors / Windows High Contrast mode. */
.button:focus-visible {
  outline: 2px solid transparent;
  outline-offset: 2px;
  box-shadow: var(--btn-focus-ring);
}

/* Link buttons have square corners at rest — round the focus ring so it isn't a rectangle. */
.link:focus-visible {
  border-radius: 0.375rem;
}

/* Icon slot — sizes any child (font glyph or svg) to the button's icon size. */
.icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: var(--btn-icon-size);
  line-height: 0;
}
.icon > svg {
  width: 1em;
  height: 1em;
}

/* On a coloured fill the glyph takes the label's colour. `Icon` resolves `var(--icon-color,
   currentColor)`, and an app may pin `--icon-color` to a muted grey for glyphs on plain surfaces
   (admin does, HS-1184) — on a brand or error fill that grey sank to ~2:1, under the 3:1 WCAG 2.2
   §1.4.11 asks of a meaningful glyph (HS-961: the icon-only "+" in the mobile list headers).
   Following `--btn-text` also greys the glyph with the label when the button is disabled. */
.primary,
.destructive,
.destructive-secondary {
  --icon-color: var(--btn-text);
}

.text {
  padding: 0 0.125rem;
}

/* Loader-only variant: hide the label (keeps its width) and center the spinner over it. */
.invisible {
  visibility: hidden;
}
.spinner-overlay {
  position: absolute;
  inset: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* ── Sizes ── */
.size-sm {
  padding: 0.5rem 0.75rem;
  font-size: var(--text-sm-font-size);
  line-height: var(--text-sm-line-height);
}
.size-md {
  padding: 0.625rem 0.875rem;
  font-size: var(--text-sm-font-size);
  line-height: var(--text-sm-line-height);
}
.size-lg {
  padding: 0.625rem 0.875rem;
  font-size: var(--text-sm-font-size);
  line-height: var(--text-sm-line-height);
}
.size-xl {
  padding: 0.75rem 1.125rem;
  gap: 0.375rem;
  font-size: var(--text-md-font-size);
  line-height: var(--text-md-line-height);
}

/* ── Icon-only square overrides ── */
.icon-only.size-sm {
  padding: 0.5rem;
}
.icon-only.size-md {
  padding: 0.625rem;
}
.icon-only.size-lg {
  padding: 0.625rem;
}
.icon-only.size-xl {
  padding: 0.75rem;
}

/* ── Primary ── */
.primary {
  --btn-bg: var(--brand-solid);
  --btn-text: var(--fg-on-solid);
  --btn-border: rgba(255, 255, 255, 0.12);
  --btn-shadow:
    0 1px 2px 0 rgba(10, 13, 18, 0.05), inset 0 -2px 0 0 rgba(10, 13, 18, 0.05),
    inset 0 0 0 1px rgba(10, 13, 18, 0.18);
  --btn-focus-ring: var(--focus-ring-shadow-xs);
  border-width: 2px;
}
.primary:hover:not(:disabled) {
  --btn-bg: var(--brand-solid-hover);
}
.primary:disabled:not(.loading) {
  --btn-bg: var(--surface-tertiary);
  --btn-text: var(--text-disabled);
  --btn-border: var(--border-light);
  --btn-shadow: none;
  border-width: 1px;
}

/* ── Secondary ── */
.secondary {
  --btn-bg: var(--surface);
  --btn-text: var(--text-secondary);
  --btn-border: var(--border);
  --btn-shadow:
    0 1px 2px 0 rgba(10, 13, 18, 0.05), inset 0 -2px 0 0 rgba(10, 13, 18, 0.05),
    inset 0 0 0 1px rgba(10, 13, 18, 0.18);
  --btn-focus-ring: var(--focus-ring-shadow-xs);
}
.secondary:hover:not(:disabled) {
  --btn-bg: var(--surface-secondary);
}
.secondary:disabled:not(.loading) {
  --btn-bg: var(--surface);
  --btn-text: var(--text-disabled);
  --btn-border: var(--border-light);
  --btn-shadow: none;
}

/* ── Tertiary ── */
.tertiary {
  --btn-bg: transparent;
  --btn-text: var(--text-tertiary);
  --btn-border: transparent;
  --btn-shadow: none;
}
.tertiary:hover:not(:disabled) {
  --btn-bg: var(--surface-secondary);
}
.tertiary:disabled:not(.loading) {
  --btn-text: var(--text-disabled);
}

/* ── Link color ── */
.link-color {
  --btn-bg: transparent;
  --btn-text: var(--brand-text);
  --btn-border: transparent;
  --btn-shadow: none;
  border-radius: 0;
}
.link.link-color {
  padding: 0;
}
.link-color:hover:not(:disabled) {
  --btn-text: var(--brand-text-hover);
}
.link-color:disabled:not(.loading) {
  --btn-text: var(--text-disabled);
}

/* ── Link gray ── */
.link-gray {
  --btn-bg: transparent;
  --btn-text: var(--text-tertiary);
  --btn-border: transparent;
  --btn-shadow: none;
  border-radius: 0;
}
.link.link-gray {
  padding: 0;
}
.link-gray:hover:not(:disabled) {
  --btn-text: var(--text-secondary);
}
.link-gray:disabled:not(.loading) {
  --btn-text: var(--text-disabled);
}

/* ── Destructive ── */
/* The fill stays the raw `--error-600`/`--error-700` in both themes, deliberately: it carries
   constant white text, and `--error-solid`'s dark half (`--error-500`) drops that pair under the
   4.5:1 WCAG 2.2 §1.4.3 asks of the label (HS-1533, caught by the dark axe scans). */
.destructive {
  --btn-bg: var(--error-600);
  --btn-text: var(--fg-on-solid);
  --btn-border: rgba(255, 255, 255, 0.12);
  --btn-shadow:
    0 1px 2px 0 rgba(10, 13, 18, 0.05), inset 0 -2px 0 0 rgba(10, 13, 18, 0.05),
    inset 0 0 0 1px rgba(10, 13, 18, 0.18);
  --btn-focus-ring: var(--focus-ring-error-shadow-xs);
  border-width: 2px;
}
.destructive:hover:not(:disabled) {
  --btn-bg: var(--error-700);
}
.destructive:disabled:not(.loading) {
  --btn-bg: var(--surface-tertiary);
  --btn-text: var(--text-disabled);
  --btn-border: var(--border-light);
  --btn-shadow: none;
  border-width: 1px;
}

/* Destructive, secondary — the outline form: same shell as `.secondary`, error border and text.
   The design draws it beside a secondary Download in a bulk bar (HS-1441). */
.destructive-secondary {
  --btn-bg: var(--surface);
  --btn-text: var(--error-chip-fg);
  --btn-border: var(--error-chip-border);
  --btn-shadow:
    0 1px 2px 0 rgba(10, 13, 18, 0.05), inset 0 -2px 0 0 rgba(10, 13, 18, 0.05),
    inset 0 0 0 1px rgba(10, 13, 18, 0.18);
  --btn-focus-ring: var(--focus-ring-error-shadow-xs);
}
.destructive-secondary:hover:not(:disabled) {
  --btn-bg: var(--error-chip-bg);
  --btn-text: var(--error-chip-fg);
}
.destructive-secondary:disabled:not(.loading) {
  --btn-bg: var(--surface);
  --btn-text: var(--text-disabled);
  --btn-border: var(--border-light);
  --btn-shadow: none;
}

/* ── Loading ── */
.loading {
  pointer-events: none;
}
.spinner {
  --spinner-size: var(--btn-icon-size);
  --spinner-track: color-mix(in srgb, currentColor, transparent 70%);
  --spinner-indicator: currentColor;
}
